import React from 'react';
import {
  FiCheckCircle,
  FiEye,
  FiRefreshCw,
  FiShare2,
  FiZap,
} from 'react-icons/fi';
import { getCEFRFromIELTS, CEFR_ORDER } from '../utils/cefrUtils';
import { sanitizeHtml } from '../utils/safeHtml';
import './EnglishTestCard.css';

/**
 * Reusable card component for English exams (Reading, Listening, Writing)
 *
 * @param {Object} props
 * @param {Object} props.test - Test data object
 * @param {string} props.testType - Type of test: 'reading', 'listening', 'writing'
 * @param {Function} props.onReview - Callback when Review button is clicked
 * @param {Function} props.onRetake - Callback when Retake button is clicked
 * @param {Function} props.onShare - Callback when Share button is clicked (optional)
 * @param {string} props.userLevel - User's diagnosed CEFR level (for performance color)
 */
export default function EnglishTestCard({
  test,
  testType,
  onReview,
  onRetake,
  onShare,
  userLevel = 'B1',
}) {
  // Extract test data
  const isCompleted = test.completed || false;

  // Extract band score with fallback to task calculations for writing
  const getExtractedBandScore = () => {
    // 1. Try direct bandScore from transformed data (already calculated)
    if (test.bandScore !== null && test.bandScore !== undefined) {
      return test.bandScore;
    }

    // 2. Try resultData.bandScore
    if (
      test.resultData?.bandScore !== null &&
      test.resultData?.bandScore !== undefined
    ) {
      return test.resultData.bandScore;
    }

    // 3. Try assessmentData.overallBandScore
    const assessmentData = test.resultData?.assessmentData;
    if (
      assessmentData?.overallBandScore !== null &&
      assessmentData?.overallBandScore !== undefined
    ) {
      return assessmentData.overallBandScore;
    }

    // 4. For writing tests: calculate from task1 and task2
    if (
      assessmentData?.task1?.assessment ||
      assessmentData?.task2?.assessment
    ) {
      const task1Band = assessmentData?.task1?.assessment?.estimated_band_score;
      const task2Band = assessmentData?.task2?.assessment?.estimated_band_score;

      if (
        task1Band !== undefined &&
        task1Band !== null &&
        task2Band !== undefined &&
        task2Band !== null
      ) {
        // IELTS Writing: Task 2 is weighted more (2/3 of overall score)
        return task1Band / 3 + (task2Band * 2) / 3;
      }
      if (task1Band !== undefined && task1Band !== null) return task1Band;
      if (task2Band !== undefined && task2Band !== null) return task2Band;
    }

    return null;
  };

  const bandScore = getExtractedBandScore();
  const score =
    test.score ?? test.resultData?.score ?? test.resultData?.rawScore;
  const totalWords = test.totalWords ?? test.resultData?.totalWords;
  const totalQuestions = test.resultData?.totalQuestions || 40;
  const completedAt =
    test.completedAt ||
    test.resultData?.completedAt ||
    test.resultData?.created_at;

  // Get CEFR level from band score
  const cefrLevel =
    bandScore !== null && bandScore !== undefined
      ? getCEFRFromIELTS(bandScore)
      : null;

  // Calculate performance color based on user's diagnosed level
  const getPerformanceColor = () => {
    if (bandScore === null || bandScore === undefined) {
      return {
        class: 'english-test-neutral',
        color: '#9e9e9e',
        bgColor: 'transparent',
      };
    }

    const testCefr = getCEFRFromIELTS(bandScore);
    const testLevelIndex = CEFR_ORDER.indexOf(testCefr.code);
    const userLevelIndex = CEFR_ORDER.indexOf(userLevel);

    if (testLevelIndex >= userLevelIndex) {
      return {
        class: 'english-test-excellent',
        color: '#22c55e',
        bgColor: 'rgba(34, 197, 94, 0.08)',
      };
    } else if (testLevelIndex === userLevelIndex - 1) {
      return {
        class: 'english-test-good',
        color: '#f59e0b',
        bgColor: 'rgba(245, 158, 11, 0.08)',
      };
    } else {
      return {
        class: 'english-test-needs-work',
        color: '#ef4444',
        bgColor: 'rgba(239, 68, 68, 0.08)',
      };
    }
  };

  const performanceColor = isCompleted ? getPerformanceColor() : null;

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Calculate percentage for reading/listening
  const getScorePercentage = () => {
    if (score === null || score === undefined) return null;
    return Math.round((score / totalQuestions) * 100);
  };

  return (
    <div
      className={`english-test-card ${isCompleted ? `english-test-completed ${performanceColor?.class || ''}` : ''}`}
      style={
        isCompleted && performanceColor
          ? {
              borderColor: performanceColor.color,
              background: performanceColor.bgColor,
            }
          : {}
      }
    >
      {/* Completed Badge Header */}
      {isCompleted && (
        <div
          className="english-test-card-badge"
          style={{
            background: `${performanceColor?.color}15`,
            color: performanceColor?.color,
          }}
        >
          <div className="english-test-badge-left">
            <FiCheckCircle size={16} />
            <span>Completed</span>
          </div>
          <div className="english-test-badge-right">
            {/* Score badge */}
            {testType === 'writing'
              ? totalWords && (
                  <span className="english-test-score-pill">
                    {totalWords} words
                  </span>
                )
              : score !== null &&
                score !== undefined && (
                  <span className="english-test-score-pill">
                    {score}/{totalQuestions}
                  </span>
                )}
            {/* Band badge with CEFR */}
            {bandScore !== null && bandScore !== undefined && (
              <span
                className="english-test-band-pill"
                style={{
                  background: cefrLevel?.color || performanceColor?.color,
                }}
              >
                Band{' '}
                {typeof bandScore === 'number'
                  ? bandScore.toFixed(1)
                  : bandScore}{' '}
                · {cefrLevel?.code}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Card Header */}
      <div className="english-test-card-header">
        <h3 className="english-test-card-title">{test.title}</h3>
        {completedAt && (
          <span className="english-test-card-date">
            {formatDate(completedAt)}
          </span>
        )}
      </div>

      {/* Card Description */}
      <div className="english-test-card-content">
        <p
          className="english-test-card-description"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(test.desc || test.description),
          }}
        />

        {/* Score Summary for completed tests */}
        {isCompleted &&
          (bandScore !== null || score !== undefined || totalWords) && (
            <div
              className="english-test-card-score-summary"
              style={{ borderLeftColor: performanceColor?.color }}
            >
              {testType === 'writing'
                ? totalWords && (
                    <span className="english-test-score-item">
                      <strong>Words:</strong> {totalWords}
                    </span>
                  )
                : score !== null &&
                  score !== undefined && (
                    <span className="english-test-score-item">
                      <strong>Score:</strong> {score}/{totalQuestions} (
                      {getScorePercentage()}%)
                    </span>
                  )}
              {bandScore !== null && bandScore !== undefined && (
                <span
                  className="english-test-score-item english-test-band"
                  style={{ color: cefrLevel?.color }}
                >
                  <strong>Band:</strong>{' '}
                  {typeof bandScore === 'number'
                    ? bandScore.toFixed(1)
                    : bandScore}{' '}
                  <span className="english-test-cefr-tag">
                    ({cefrLevel?.code} - {cefrLevel?.name})
                  </span>
                </span>
              )}
            </div>
          )}
      </div>

      {/* Card Actions */}
      <div className="english-test-card-actions">
        {isCompleted ? (
          <>
            <button
              className="english-test-btn-secondary"
              onClick={() => onReview?.(test)}
            >
              <FiEye size={16} /> Review
            </button>
            <button
              className="english-test-btn-primary"
              onClick={() => onRetake?.(test)}
            >
              <FiRefreshCw size={16} /> Retake
            </button>
          </>
        ) : (
          <>
            <button
              className="english-test-btn-primary"
              onClick={() => onRetake?.(test)}
            >
              <FiZap size={16} /> Start
            </button>
            {onShare && (
              <button
                className="english-test-btn-outline"
                onClick={() => onShare?.(test)}
              >
                <FiShare2 size={16} /> Share
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
